// frontend/src/components/AppUpdateNotification.js
import React, { useState, useEffect, useCallback } from 'react';
import { Button, theme } from 'antd'; // Removed notification, Space
import { DownloadOutlined, ReloadOutlined, InfoCircleOutlined } from '@ant-design/icons';
import * as serviceWorkerRegistration from '../serviceWorkerRegistration';
import { useNotification } from '../context/NotificationContext';
import { useTranslation } from 'react-i18next';

const AppUpdateNotification = () => {
    const { notification: notify } = useNotification(); // Removed modal
    const { t } = useTranslation();
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    
    // State to hold the Service Worker registration object when an update is found
    const [waitingWorker, setWaitingWorker] = useState(null);
    
    // --- PWA Installation Prompt Logic (Kept for completeness) ---
    useEffect(() => {
        const handler = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
        };
        window.addEventListener('beforeinstallprompt', handler);
        return () => {
            window.removeEventListener('beforeinstallprompt', handler);
        };
    }, []);

    const handleInstallClick = async () => {
        if (!deferredPrompt) return;
        deferredPrompt.prompt();
        await deferredPrompt.userChoice;
        setDeferredPrompt(null);
    };
    // ------------------------------------------------------------


    // --- Service Worker Update Logic ---
    const updateApp = useCallback(() => {
        if (!waitingWorker) return;

        // 1. Tell the Service Worker to skip waiting and activate
        waitingWorker.postMessage({ type: 'SKIP_WAITING' });

        // 2. Force a full page reload to load the new assets
        waitingWorker.addEventListener('statechange', (event) => {
            if (event.target.state === 'activated') {
                window.location.reload();
            }
        });
        
        // Fallback reload if state change listener fails or is missed
        setTimeout(() => window.location.reload(), 100);
        
    }, [waitingWorker]);
    const { token } = theme.useToken(); // Add this line

    const showUpdateNotification = useCallback((registration) => {
        // The waiting worker is the new version of the SW
        const worker = registration.waiting;
        if (worker) {
            setWaitingWorker(worker);
            
            const key = `update-${new Date().getTime()}`;
            
            const btn = (
                <Button 
                    type="default" 
                    icon={<ReloadOutlined />} 
                    onClick={() => {
                        notify.destroy(key);
                        updateApp();
                    }}
                >
                    {t('library.updateReload')}
                </Button>
            );

            notify.open({
                message: t('library.newUpdate'),
                description: t('library.updateDesc'),
                btn,
                key,
                duration: 0, // Persistent notification until action is taken
                icon: <InfoCircleOutlined style={{ color: token.colorPrimary }} /> // Fixed: token now defined
            });
        }
    }, [notify, updateApp, token, t]);
    // ------------------------------------------------------------


    useEffect(() => {
        // Register the service worker with the update callback
        serviceWorkerRegistration.register({
            onUpdate: showUpdateNotification,
        });
    }, [showUpdateNotification]);


    const isAppInstalled = window.matchMedia('(display-mode: standalone)').matches;

    return (
        <>
            {/* Show PWA Install Button if deferredPrompt is available and app is not installed */}
            {!isAppInstalled && deferredPrompt && (
                <Button 
                    type="default" 
                    icon={<DownloadOutlined />} 
                    onClick={handleInstallClick}
                    style={{ position: 'fixed', bottom: '80px', right: '20px', zIndex: 1000 }}
                >
                    {t('library.installApp')}
                </Button>
            )}
            {/* The update notification is handled by the AntD notification system */}
        </>
    );
};

export default AppUpdateNotification;